import { NOTIFICATION_GENERATION_POLICY_DISPOSITIONS } from '@pushly/models/lib/structs/pushly-ai/notification-generation-policy-error'
import type { NotificationGenerationPolicyDisposition } from '@pushly/models/lib/structs/pushly-ai/notification-generation-policy-error'
import type { BrandVoiceOwner, BrandVoiceOwnerDeps, GenerateVariantsAnswer } from '../../../modules/ai/brand-voice'
import { currentBrandVoiceOwner } from './brand-voice-owner-registry'

/**
 * Debug helpers for looking at the Brand Voice preview in a state that is hard to reach, from the browser console.
 * They do not draw anything over the page. Each one swaps a dependency of the open Brand Voice owner for one that
 * fails, never answers or answers with a sample, then asks the owner for the preview or the segments the normal way,
 * so the screen reaches the state through the real path. No request leaves the browser while a dependency is
 * swapped, and nothing is saved.
 *
 * The preview helpers share their names with the Curator's `__aiSettingsDebug`, so the two can become one set that
 * acts on whichever preview is open.
 *
 * Usage (from the browser console, on the Brand Voice page):
 *   __brandVoiceDebug.setPreviewNoRequest()          // nothing has been previewed yet
 *   __brandVoiceDebug.setPreviewLoading()            // a preview is running and never finishes
 *   __brandVoiceDebug.setPreviewNoResult()           // it ran and Brand Voice wrote nothing
 *   __brandVoiceDebug.setPreviewError()              // it could not be run
 *   __brandVoiceDebug.setPreviewResult()             // it ran and has a notification to draw
 *   __brandVoiceDebug.setPreviewResultLongText()     // the same, with a long title, body and reason
 *   __brandVoiceDebug.setPreviewPageError()          // the page could not be read, with a link
 *   __brandVoiceDebug.setPreviewAdvisory('exhausted') // drawn, but not confirmed: exhausted, not_checked, retry_failed
 *   __brandVoiceDebug.setPreviewSegmentsLoading()    // the segments never finish loading (before a first load only)
 *   __brandVoiceDebug.setPreviewSegmentsError()      // the segments could not be loaded (before a first load only)
 *   __brandVoiceDebug.setPreviewNoSegments()         // the domain has no segments
 *   __brandVoiceDebug.resetPreview()                 // the real dependencies again
 *   __brandVoiceDebug.status()                       // print what is swapped
 *   __brandVoiceDebug.owner()                        // the open owner: read its projections, dispatch its intents
 */
let undos: (() => void)[] = []
let swapped: string[] = []
let actedOn: BrandVoiceOwner | undefined

const LOG = '[BrandVoiceDebug]'

/** The page the forced previews are for. Nothing is fetched from it. */
const SAMPLE_URL = 'https://example.com/sample-article'

const withOwner = <T>(act: (owner: BrandVoiceOwner) => T): T | undefined => {
    const owner = currentBrandVoiceOwner()

    if (!owner) {
        console.warn(`${LOG} Open the Brand Voice page first: there is no owner to act on.`)
        return undefined
    }

    // Swaps belong to the owner they were made on, so another domain starts with none.
    if (owner !== actedOn) {
        undos = []
        swapped = []
        actedOn = owner
    }

    return act(owner)
}

const swap = (owner: BrandVoiceOwner, name: string, overrides: Parameters<BrandVoiceOwner['swapDeps']>[0]) => {
    undos = [...undos, owner.swapDeps(overrides)]
    swapped = [...swapped, name]
}

const variant = (overrides: Record<string, unknown> = {}) =>
    ({
        title: 'A sample notification, as Brand Voice would write it',
        body: 'Forced from the console. No request was made and nothing was generated.',
        image: null,
        keywords: null,
        tone: 'calm',
        reason: 'A sample reason: why Brand Voice chose this angle for the page and the segment.',
        brand_voice_applied: true,
        ...overrides,
    }) as any

const LONG_VARIANT = variant({
    title: 'A sample notification with a headline long enough that it has to wrap or be cut off on every platform the preview can draw',
    body: 'Forced from the console. This body runs on for several sentences so the preview shows how each platform and state treats text that does not fit. It keeps going past what any notification would show, and then a little further, to be sure the end is reached.',
    reason: 'A sample reason that runs long, to show how the Generation Details panel wraps beside a preview: the page is about a topic this segment has clicked on all week, so the title leads with it, and the body keeps to the calm, editorial tone the Brand Voice asks for rather than leaning on urgency.',
})

const answer = (overrides: Partial<GenerateVariantsAnswer> = {}): GenerateVariantsAnswer => ({
    variants: [variant()],
    error: null,
    policy_error: null,
    ...overrides,
})

const advisoryAnswer = (disposition: NotificationGenerationPolicyDisposition): GenerateVariantsAnswer =>
    answer({
        policy_error: {
            tier: 'checked',
            attempts: 3,
            disposition,
            violations: [
                {
                    variant_index: 0,
                    tier: 'guaranteed',
                    rule_label: 'Never use exclamation points',
                    reason: 'The title ends with one.',
                    title: '',
                    body: '',
                },
                {
                    variant_index: 0,
                    tier: 'checked',
                    rule_label: 'Favor curiosity over hype',
                    reason: 'The body leans on urgency rather than the story.',
                    title: '',
                    body: '',
                },
            ],
        } as any,
    })

/** The segment the preview would open on: the domain default, or the first one. */
const previewSegmentId = (owner: BrandVoiceOwner): number | undefined => {
    if (owner.segments.status !== 'ready' && owner.segments.status !== 'refreshFailed') {
        return undefined
    }

    const segments = owner.segments.data

    return (segments.find((segment) => segment.isDefault) ?? segments[0])?.id
}

const preview = (name: string, generateVariants: BrandVoiceOwnerDeps['generateVariants']) =>
    withOwner((owner) => {
        const segmentId = previewSegmentId(owner)

        if (!segmentId) {
            console.warn(`${LOG} The segments have not loaded, so there is nothing to preview for.`)
            return
        }

        swap(owner, name, { generateVariants })
        void owner.intents['brandVoice.preview'].perform({ segmentId, url: SAMPLE_URL })
        console.info(`${LOG} Previewing with ${name}.`)
    })

const segments = (
    name: string,
    fetchSegments: BrandVoiceOwnerDeps['fetchSegments'],
    { keepsLoadedList }: { keepsLoadedList: boolean },
) =>
    withOwner((owner) => {
        // A read that fails or never answers keeps the list already shown, by design, so once the segments have loaded
        // these states only show on a domain whose first read is the swapped one.
        if (keepsLoadedList && (owner.segments.status === 'ready' || owner.segments.status === 'refreshFailed')) {
            console.warn(
                `${LOG} The segments are already loaded, and a failed or unanswered read keeps them, so ${name} will not show. ` +
                    'It shows only when the first read for a domain is the swapped one.',
            )
        }

        swap(owner, name, { fetchSegments })
        void owner.loadSegments()
        console.info(`${LOG} Loading the segments with ${name}.`)
    })

export const brandVoiceDebug = {
    setPreviewNoRequest: () =>
        withOwner((owner) => {
            void owner.intents['brandVoice.resetPreview'].perform()
            console.info(`${LOG} Showing the preview before anything has been run.`)
        }),
    setPreviewLoading: () => preview('a preview that never answers', () => new Promise(() => undefined)),
    setPreviewNoResult: () =>
        preview('a preview with nothing written', async () => ({ ok: true, data: answer({ variants: [] }) })),
    setPreviewError: () => preview('a preview that fails', async () => ({ ok: false })),
    setPreviewResult: () => preview('a sample notification', async () => ({ ok: true, data: answer() })),
    setPreviewResultLongText: () =>
        preview('a sample notification with long text', async () => ({
            ok: true,
            data: answer({ variants: [LONG_VARIANT] }),
        })),
    setPreviewPageError: () =>
        preview('a page that could not be read', async () => ({
            ok: true,
            data: answer({
                variants: [],
                error: {
                    message: 'The page is behind a paywall, so its content could not be read.',
                    status_code: 403,
                    reference_url: 'https://docs.pushly.com',
                } as any,
            }),
        })),
    setPreviewAdvisory: (disposition: NotificationGenerationPolicyDisposition = 'exhausted') => {
        if (!NOTIFICATION_GENERATION_POLICY_DISPOSITIONS.includes(disposition)) {
            console.warn(`${LOG} Use one of: ${NOTIFICATION_GENERATION_POLICY_DISPOSITIONS.join(', ')}.`)
            return
        }

        preview(`a notification with a ${disposition} advisory`, async () => ({
            ok: true,
            data: advisoryAnswer(disposition),
        }))
    },
    setPreviewSegmentsLoading: () =>
        segments('segments that never load', () => new Promise(() => undefined), { keepsLoadedList: true }),
    setPreviewSegmentsError: () =>
        segments('segments that fail', async () => ({ ok: false }), { keepsLoadedList: true }),
    setPreviewNoSegments: () =>
        segments('no segments', async () => ({ ok: true, data: [] }), { keepsLoadedList: false }),
    resetPreview: () =>
        withOwner((owner) => {
            undos.forEach((undo) => undo())
            undos = []
            swapped = []
            void owner.intents['brandVoice.resetPreview'].perform()
            void owner.loadSegments()
            console.info(`${LOG} The preview is using its real dependencies.`)
        }),
    status: () => {
        console.info(`${LOG} ${swapped.length ? `swapped: ${swapped.join(', ')}` : 'nothing swapped'}`)

        return swapped
    },
    owner: () => withOwner((owner) => owner),
}

export const installBrandVoiceDebug = (
    target: Window | undefined = typeof window === 'undefined' ? undefined : window,
) => {
    if (!target) {
        return
    }

    ;(target as unknown as { __brandVoiceDebug?: typeof brandVoiceDebug }).__brandVoiceDebug = brandVoiceDebug
}
